import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext.jsx';
import { fn, logWebEvent } from '../firebase.js';
import { fetchQuestion, mergeUserAnswers } from '../data/dailySet.js';
import { writeAnswer } from '../util/playContract.js';
import QuestionCard from '../components/QuestionCard.jsx';
import { SkeletonCard } from '../components/Skeleton.jsx';
import Avatar from '../components/Avatar.jsx';

// /q/<id> — the single-question door. A recipient taps a shared link and
// is answering in under 10 seconds: no install, no account, no code.
// After they answer, upsell to /play ("that's 1 of today's 6").
//
// No live consensus reveal (iOS-only). The post-answer beat here is the
// upsell + the honest "we'll ping you when it resolves" install hook.
export default function Question() {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { status, uid, claimUserDoc } = useSession();
  const [question, setQuestion] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | ready | notfound | error
  const [busy, setBusy] = useState(false);
  // VP-4 — a Predictor's question share: /q/<id>?f=<handle>. Unlike ?h2h=,
  // this does NOT redirect: the whole point is that they answer the question
  // first. `predictor` is the tease (name + avatar, never the pick);
  // `reveal` arrives only after they commit.
  const [predictor, setPredictor] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followed, setFollowed] = useState(false);

  // Share-to-game: /q/<id>?h2h=<inviteId> — the Today "Play with a Friend" flow
  // shares the question-image link carrying a pending H2H invite. A human tap
  // routes into the invite accept flow (JoinConfirmation → "Game on"); the /q OG
  // unfurl still shows the question card, so only the tapper gets redirected.
  useEffect(() => {
    const h2h = new URLSearchParams(window.location.search).get('h2h');
    if (h2h && /^[A-Za-z0-9_-]{6,40}$/.test(h2h)) {
      navigate(`/i/${h2h}`, { replace: true });
    }
  }, [navigate]);

  // ?f=<handle> — a Verified Predictor's share. Deliberately NOT a redirect
  // (the ?h2h= branch above is): bouncing to /f/<handle> would skip the
  // question, and the question IS the invitation. Resolve the Predictor for the
  // tease; their PICK is never fetched here — revealPredictorAnswer refuses to
  // return it until this visitor has made their own call, so it can't leak into
  // a network response the curious could read before committing.
  const followHandle = new URLSearchParams(window.location.search).get('f');
  useEffect(() => {
    if (!followHandle || status !== 'ready') return;
    let cancelled = false;
    fn.resolveFollowLink({ handle: followHandle })
      .then((r) => {
        if (!cancelled && r?.data?.creator) {
          setPredictor(r.data.creator);
          setFollowed(!!r.data.alreadyFollowing);
          logWebEvent('web_predictor_question_landed', { handle: followHandle });
        }
      })
      .catch(() => {
        /* unknown handle → plain question page, which is still a fine landing */
      });
    return () => {
      cancelled = true;
    };
  }, [followHandle, status]);

  const load = useCallback(async () => {
    if (status !== 'ready' || !uid) return;
    setPhase('loading');
    try {
      const q = await fetchQuestion(questionId);
      if (!q) {
        setPhase('notfound');
        return;
      }
      const [merged] = await mergeUserAnswers(uid, [q]);
      setQuestion(merged);
      setPhase('ready');
    } catch (e) {
      console.error('[Question] load failed:', e);
      setPhase('error');
    }
  }, [status, uid, questionId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAnswer = useCallback(
    async (answer) => {
      if (busy || !question) return;
      setBusy(true);
      const prev = question;
      setQuestion((q) => ({ ...q, answer, isSkipped: false, answered: true }));
      try {
        await claimUserDoc('Guest');
        await writeAnswer(uid, question, { answer, isSkipped: false });
        // The payoff. Only now will the server say — the write above is the
        // key that unlocks it (revealPredictorAnswer gates on this answer
        // existing). Best-effort: a failed reveal leaves the answer recorded
        // and the Follow CTA still shows; losing the comparison is a smaller
        // loss than losing the call.
        if (predictor) {
          try {
            const r = await fn.revealPredictorAnswer({ questionId: question.id, handle: followHandle });
            if (r?.data?.answered) setReveal(r.data);
          } catch {
            /* non-fatal — see above */
          }
        }
      } catch (e) {
        console.error('[Question] writeAnswer failed:', e);
        setQuestion(prev);
      } finally {
        setBusy(false);
      }
    },
    [busy, question, uid, claimUserDoc, predictor, followHandle]
  );

  const doFollow = useCallback(async () => {
    if (following || !predictor) return;
    setFollowing(true);
    try {
      await fn.followCreator({ creatorUid: predictor.uid });
      logWebEvent('web_predictor_question_followed', { handle: followHandle });
      setFollowed(true);
    } catch {
      setFollowing(false);
    }
  }, [following, predictor, followHandle]);

  if (status === 'starting' || phase === 'loading') {
    return (
      <main className="ps-page" aria-busy="true" aria-label="Loading question">
        <SkeletonCard image lines={2} />
      </main>
    );
  }

  if (phase === 'notfound') {
    return (
      <main className="ps-page ps-center">
        <div className="ps-card">
          <p style={{ fontWeight: 800, marginTop: 0 }}>Question not found</p>
          <p className="ps-muted" style={{ marginBottom: 14 }}>
            This one may have rolled off. Try today’s six instead.
          </p>
          <Link to="/play" style={ctaLink}>
            Play today’s six →
          </Link>
        </div>
      </main>
    );
  }

  if (phase === 'error') {
    return (
      <main className="ps-page ps-center">
        <p className="ps-muted">Something went wrong.</p>
        <button onClick={load} style={retryBtn}>
          Try again
        </button>
      </main>
    );
  }

  const answered = question.answered;
  // Handle-first identity (audit R28, mirrors FollowLanding's 2026-07-20
  // rule): the tapper met this Predictor as an @handle on the share — the
  // internal displayName is a second, unfamiliar name mid-funnel.
  const predictorLabel = predictor
    ? (predictor.handle ? `@${predictor.handle}` : predictor.displayName)
    : '';

  return (
    <main className="ps-page">
      <p className="ps-muted ps-center" style={{ margin: '4px 0 14px' }}>
        {question.isResolved ? 'This one’s settled' : answered ? 'Your call is in' : 'What’s your call?'}
      </p>

      {/* The tease: names the Predictor and says a call is LOCKED — never what
          it is. "@tom says YES" would hand the reader the payload with nothing
          left to tap for; the curiosity gap is the whole reason this link
          travels. Same rule as the count-only rivalry pulse. */}
      {predictor && !answered && !question.isResolved && (
        <div style={teaseCard}>
          <Avatar
            uid={predictor.uid}
            name={predictor.displayName}
            avatarId={predictor.equippedAvatar}
            frameId={predictor.equippedFrame}
            size={38}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14.5 }}>
              {predictorLabel} has locked their call
            </div>
            <div className="ps-muted" style={{ fontSize: 12.5 }}>
              make yours to see it
            </div>
          </div>
        </div>
      )}

      <QuestionCard
        question={question}
        disabled={busy || answered || question.isResolved}
        onAnswer={handleAnswer}
      />

      {/* The payoff — their first League comparison, ten seconds after a post. */}
      {reveal?.predictorAnswered && (
        <div style={{ ...teaseCard, display: 'block', textAlign: 'center', marginTop: 4 }}>
          <div style={{ fontSize: 26, marginBottom: 4 }}>{reveal.agreed ? '🤝' : '⚡️'}</div>
          <div style={{ fontWeight: 800, fontSize: 17 }}>
            {predictorLabel} said{' '}
            <span style={{ color: 'var(--gold)' }}>{reveal.predictorAnswer ? 'YES' : 'NO'}</span>.
            {' '}You said{' '}
            <span style={{ color: 'var(--gold)' }}>{question.answer ? 'YES' : 'NO'}</span>.
          </div>
          <div className="ps-muted" style={{ fontSize: 13, margin: '4px 0 12px' }}>
            {reveal.agreed
              ? 'You two read this one the same way.'
              : 'You’re on opposite sides. One of you is about to be right.'}
          </div>
          {followed ? (
            <div style={{ color: 'var(--gold)', fontWeight: 800, fontSize: 14 }}>
              Following @{predictor.handle} — your League is live
            </div>
          ) : (
            <button onClick={doFollow} disabled={following} style={{ ...ctaBtn, opacity: following ? 0.6 : 1 }}>
              {following ? 'Following…' : `Follow @${predictor.handle}`}
            </button>
          )}
        </div>
      )}

      {/* They shared it but haven't locked their own six yet — say so plainly
          rather than inventing a comparison, and still offer the League. */}
      {reveal && reveal.predictorAnswered === false && !followed && predictor && (
        <div style={{ ...teaseCard, display: 'block', textAlign: 'center', marginTop: 4 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
            {predictorLabel} hasn’t locked this one yet
          </div>
          <div className="ps-muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Follow to see how your week stacks up against theirs.
          </div>
          <button onClick={doFollow} disabled={following} style={{ ...ctaBtn, opacity: following ? 0.6 : 1 }}>
            {following ? 'Following…' : `Follow @${predictor.handle}`}
          </button>
        </div>
      )}

      {/* A3 (UX flywheel) — settled question mirrors questionUnfurl's rule:
          show the outcome, then point at today's live six. No live consensus
          on this public surface (paid-reveal rule); the answer is a fact. */}
      {question.isResolved && (
        <div className="ps-card ps-center" style={{ marginTop: 4 }}>
          <p style={{ fontWeight: 800, fontSize: 17, margin: '0 0 6px' }}>
            The answer:{' '}
            <strong style={{ color: 'var(--gold)' }}>
              {question.correctAnswer ? 'YES' : 'NO'}
            </strong>
          </p>
          <p className="ps-muted" style={{ margin: '0 0 14px', lineHeight: 1.5 }}>
            {predictor
              ? `Too late to call this one — but ${predictorLabel} plays the daily six. Follow to compare yours.`
              : 'Would you have called it? Today’s six are live on Predict Six.'}
          </p>
          {/* A fan who taps after the question settles can't answer, so there's
              no reveal to earn — but arriving late shouldn't be a dead end.
              The League is still the thing they came for. */}
          {predictor && !followed && (
            <button onClick={doFollow} disabled={following}
              style={{ ...ctaBtn, marginBottom: 10, opacity: following ? 0.6 : 1 }}>
              {following ? 'Following…' : `Follow @${predictor.handle}`}
            </button>
          )}
          <button
            onClick={() => {
              logWebEvent('web_q_upsell_tapped', { questionId, resolved: true });
              navigate('/play');
            }}
            style={ctaBtn}
          >
            Play today’s six →
          </button>
        </div>
      )}

      {answered && !question.isResolved && (
        <div className="ps-card ps-center" style={{ marginTop: 4 }}>
          <p style={{ fontWeight: 800, fontSize: 17, margin: '0 0 6px' }}>
            That’s 1 of today’s 6.
          </p>
          {/* A3 — resolution-timing-aware tease: the pick is saved and scores
              as its event concludes. No "tonight"/"verdict" for a fresh pick —
              today's picks settle on FUTURE days, not the night they're made. */}
          <p className="ps-muted" style={{ margin: '0 0 10px', lineHeight: 1.5 }}>
            🔒 Locked in — results as the events unfold. Finish today’s six and we’ll score you.
          </p>
          <p className="ps-muted" style={{ margin: '0 0 14px', lineHeight: 1.5 }}>
            Lock in all six to <strong style={{ color: 'var(--gold)' }}>earn coins and start your streak</strong> — this one’s only counted once you complete the set.
          </p>
          <button
            onClick={() => {
              logWebEvent('web_q_upsell_tapped', { questionId });
              // Carry this question into /play (?q floats it to the top); the
              // answer just cast is already saved, so they finish the other 5
              // and the all-six gate awards the set.
              navigate(`/play?q=${questionId}`);
            }}
            style={ctaBtn}
          >
            Finish today’s six →
          </button>
        </div>
      )}
    </main>
  );
}

const teaseCard = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--card-bg)',
  border: '1px solid var(--gold)',
  borderRadius: 14,
  padding: '12px 14px',
  marginBottom: 10,
  textAlign: 'left',
};
const ctaBtn = {
  padding: '12px 20px',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--gold)',
  color: '#1a1a1a', // near-black on gold for AA contrast (audit A2)
  fontWeight: 800,
  fontSize: 15,
};
const ctaLink = { ...ctaBtn, display: 'inline-block', textDecoration: 'none' };
const retryBtn = {
  marginTop: 10,
  padding: '10px 18px',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--cta-bg)',
  color: 'var(--cta-text)',
  fontWeight: 700,
};
